import React, { useState, useRef, useCallback, useEffect } from 'react';

interface VideoPlayerProps {
  src: string;
  fileName: string;
  onError?: (error: string) => void;
  onClose?: () => void;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  fileName,
  onError,
  onClose
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const formatTime = (time: number): string => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlay = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(_err => {
          const errorMsg = 'Failed to play video';
          setError(errorMsg);
          onError?.(errorMsg);
        });
      }
    }
  }, [isPlaying, onError]);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
    }
  }, []);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!videoRef.current) return;

    if (!isFullscreen) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, [isFullscreen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.code) {
      case 'Space':
        e.preventDefault();
        handlePlay();
        break;
      case 'KeyF':
        e.preventDefault();
        handleFullscreen();
        break;
      case 'Escape':
        if (onClose) {
          onClose();
        }
        break;
    }
  }, [handlePlay, handleFullscreen, onClose]);

  // Auto-hide controls after inactivity
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    const resetTimeout = () => {
      setShowControls(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (isPlaying) {
          setShowControls(false);
        }
      }, 3000);
    };

    const handleMouseMove = () => resetTimeout();
    
    if (isPlaying) {
      resetTimeout();
      document.addEventListener('mousemove', handleMouseMove);
    } else {
      setShowControls(true);
    }

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isPlaying]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    const handleError = () => {
      const errorMsg = 'Failed to load video file';
      setError(errorMsg);
      setIsLoading(false);
      onError?.(errorMsg);
    };

    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [onError]);

  if (error) {
    return (
      <div className="video-player-error flex items-center justify-center h-64 bg-red-50 border border-red-200 rounded">
        <div className="text-center">
          <div className="text-red-500 text-3xl mb-2">🎬❌</div>
          <p className="text-red-700 text-sm">{error}</p>
          {onClose && (
            <button
              onClick={onClose}
              className="mt-2 px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div 
      className="video-player-container fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="relative w-full h-full max-w-6xl max-h-full">
        {/* Header */}
        <div className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black to-transparent p-4 z-10 transition-opacity ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-white text-xl">🎬</span>
              <h3 className="text-white font-medium">{fileName}</h3>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="text-white hover:text-red-400 text-2xl"
                title="Close (Esc)"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Video */}
        <video
          ref={videoRef}
          src={src}
          className="w-full h-full object-contain"
          onClick={handlePlay}
          preload="metadata"
        />

        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="text-white text-center">
              <div className="text-4xl mb-2">⏳</div>
              <p>Loading video...</p>
            </div>
          </div>
        )}

        {/* Play button overlay */}
        {!isLoading && !isPlaying && (
          <div 
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            onClick={handlePlay}
          >
            <div className="bg-black bg-opacity-50 rounded-full p-4 hover:bg-opacity-70">
              <div className="text-white text-6xl">▶️</div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 transition-opacity ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}>
          {/* Progress bar */}
          <div className="mb-4">
            <input
              type="range"
              min={0}
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              disabled={isLoading}
              className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer video-slider"
            />
            <div className="flex justify-between text-xs text-white mt-1">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Control buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handlePlay}
                disabled={isLoading}
                className={`flex items-center justify-center w-10 h-10 rounded-full text-white text-lg ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white hover:bg-opacity-20'
                }`}
                title="Play/Pause (Space)"
              >
                {isLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
              </button>

              {/* Volume control */}
              <div className="flex items-center gap-2">
                <span className="text-white text-sm">🔊</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.1}
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleFullscreen}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded"
                title="Fullscreen (F)"
              >
                {isFullscreen ? '🔲' : '⛶'}
              </button>
            </div>
          </div>

          {/* Keyboard shortcuts help */}
          <div className="text-xs text-gray-400 mt-2 text-center">
            Space: Play/Pause • F: Fullscreen • Esc: Close
          </div>
        </div>
      </div>

      <style>{`
        .video-slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
        }
        
        .video-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};